import { useCallback, useRef, useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/Button';
import { ProgressBar } from '../components/ui/ProgressBar';
import { IconArrowRight, IconUpload } from '../components/ui/icons';
import { ApiError, uploads, putToR2 } from '../lib/api';
import { track } from '../lib/analytics';
import { apiErrorMessage } from '../lib/errors';
import { useToast } from '../components/ui/Toast';

const MAX_BYTES = 200 * 1024 * 1024;
const ACCEPT = 'audio/wav,audio/wave,audio/x-wav,audio/mpeg,audio/mp3,audio/aac,audio/x-m4a,audio/mp4,audio/flac,audio/x-flac,audio/ogg,.wav,.aiff,.aif,.mp3';
const ALLOWED_TYPES = new Set([
  'audio/wav', 'audio/wave', 'audio/x-wav', 'audio/mpeg', 'audio/mp3',
  'audio/aac', 'audio/x-m4a', 'audio/mp4', 'audio/flac', 'audio/x-flac', 'audio/ogg',
]);

type Phase = 'idle' | 'uploading' | 'confirming' | 'done' | 'error';

export function UploadStudio() {
  const { t, fmt } = useI18n();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const guessType = (f: File): string | null => {
    if (f.type && ALLOWED_TYPES.has(f.type)) return f.type;
    const ext = f.name.toLowerCase().split('.').pop();
    if (ext === 'wav') return 'audio/wav';
    if (ext === 'mp3') return 'audio/mpeg';
    if (ext === 'aiff' || ext === 'aif') return 'audio/x-wav'; // AIFF sent as wav-family; backend re-detects
    if (ext === 'flac') return 'audio/flac';
    if (ext === 'm4a') return 'audio/mp4';
    if (ext === 'ogg') return 'audio/ogg';
    return f.type || null;
  };

  const startUpload = useCallback(
    async (f: File) => {
      setError(null);
      if (f.size > MAX_BYTES) {
        setError(t.errors.tooLarge);
        setPhase('error');
        setFile(f);
        return;
      }
      const contentType = guessType(f);
      if (!contentType || !ALLOWED_TYPES.has(contentType)) {
        setError(t.errors.unsupported);
        setPhase('error');
        setFile(f);
        return;
      }

      setFile(f);
      setLoaded(0);
      setTotal(f.size);
      setPhase('uploading');
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const presigned = await uploads.presign({
          filename: f.name.slice(0, 255),
          content_type: contentType,
          byte_size: f.size,
          kind: 'audio_source',
        });
        await putToR2(presigned.upload_url, f, presigned.content_type, (l, tt) => {
          setLoaded(l);
          setTotal(tt);
        }, controller.signal);

        setPhase('confirming');
        const confirmed = await uploads.confirm(presigned.upload_id);
        setJobId(confirmed.job_id);
        setPhase('done');
        track('upload_confirmed', {
          job_id: confirmed.job_id,
          asset_id: confirmed.asset_id,
          byte_size: f.size,
          content_type: contentType,
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          setPhase('idle');
          setFile(null);
          return;
        }
        const msg = apiErrorMessage(err, t);
        setError(msg);
        setPhase('error');
        if (err instanceof ApiError && err.isRateLimited) toast.push(msg, 'error');
      } finally {
        abortRef.current = null;
      }
    },
    [t, toast],
  );

  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files?.[0];
      if (f) void startUpload(f);
    },
    [startUpload],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    setPhase('idle');
    setFile(null);
    setError(null);
    setJobId(null);
    setLoaded(0);
    setTotal(0);
  }, []);

  const pct = total > 0 ? (loaded / total) * 100 : 0;

  return (
    <div>
      <PageHeader title={t.upload.title} subtitle={t.upload.subtitle} />

      {phase === 'done' && jobId ? (
        <div className="rounded border border-success bg-surface-2 p-8 text-center">
          <h2 className="type-heading-1 text-primary">{t.upload.successTitle}</h2>
          <p className="type-body text-secondary mt-2">{t.upload.successBody}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to={`/review/${jobId}`}>
              <Button size="lg" iconRight={<IconArrowRight size={18} />}>
                {t.upload.goToReview}
              </Button>
            </Link>
            <Button variant="ghost" size="lg" onClick={reset}>
              {t.upload.another}
            </Button>
          </div>
        </div>
      ) : phase === 'uploading' || phase === 'confirming' ? (
        <div className="rounded border border-border-interactive bg-surface-1 p-6" aria-live="polite">
          <div className="flex items-center justify-between gap-4">
            <span className="type-data text-primary truncate">{file?.name}</span>
            <span className="type-data text-muted tnum shrink-0">
              {phase === 'confirming' ? t.upload.processing : t.upload.ofSize(fmt.bytes(loaded), fmt.bytes(total))}
            </span>
          </div>
          <div className="mt-4">
            <ProgressBar value={phase === 'confirming' ? 100 : pct} label={t.upload.uploading} />
          </div>
          <div className="mt-4">
            <Button variant="ghost" size="md" onClick={cancel} disabled={phase === 'confirming'}>
              {t.upload.cancel}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={[
              'flex min-h-dropzone cursor-pointer flex-col items-center justify-center gap-3 rounded p-8 text-center',
              'border-2 border-dashed transition-colors duration-fast ease-machine',
              dragOver ? 'border-accent bg-accent-tint' : 'border-border-interactive bg-surface-1',
            ].join(' ')}
          >
            <span className={dragOver ? 'text-accent' : 'text-secondary'}>
              <IconUpload size={40} />
            </span>
            <span className="type-heading-2 text-primary">{t.upload.dropTitle}</span>
            <span className="type-body-sm text-muted">{t.upload.dropHint}</span>
            <span className="mt-2">
              <Button variant="secondary" size="md" onClick={() => inputRef.current?.click()} type="button">
                {t.upload.browse}
              </Button>
            </span>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void startUpload(f);
                e.target.value = '';
              }}
            />
          </label>

          {phase === 'error' && error ? (
            <p className="type-body-sm text-danger mt-4 flex items-center gap-2" role="alert">
              {error}
              <button type="button" className="type-label underline focus-ring rounded-sm" onClick={() => file && startUpload(file)}>
                {t.common.retry}
              </button>
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
